import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useI18n, SUPPORTED_LANGS } from "@/i18n/I18nContext";

const NAV_LINKS = [
  { key: "about", target: "about" },
  { key: "howItWorks", target: "how" },
  { key: "benefits", target: "benefits" },
  { key: "faq", target: "faq" },
];

export default function Header() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang: routeLang } = useParams();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const activeLang = routeLang || lang;

  useEffect(() => {
    setOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goSection = (id) => {
    const homePath = `/${activeLang}`;
    if (location.pathname !== homePath) {
      navigate(`${homePath}#${id}`);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ block: "start" });
      window.history.replaceState(null, "", `${homePath}#${id}`);
    }
    setOpen(false);
  };

  const switchLang = (nextLang) => {
    if (!SUPPORTED_LANGS.includes(nextLang)) return;
    const stripped = location.pathname.replace(/^\/(ec|en)(?=\/|$)/, "");
    const nextPath = `/${nextLang}${stripped || ""}` + location.hash;
    navigate(nextPath, { replace: false });
    setLangOpen(false);
    setOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-slate-950 border-b border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link
          to={`/${activeLang}`}
          data-testid="header-logo"
          aria-label={t("aria.goToHome")}
          className="flex items-center gap-2 text-white"
        >
          <LogoMark />
          <span className="font-bold tracking-tight text-lg">Ecuproyecta</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <button
              key={link.key}
              data-testid={`nav-link-${link.key}`}
              onClick={() => goSection(link.target)}
              className="px-3 py-2 text-sm text-slate-300 hover:text-white transition-colors"
            >
              {t(`nav.${link.key}`)}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative" ref={langRef}>
            <button
              data-testid="lang-switcher-button"
              onClick={() => setLangOpen((v) => !v)}
              aria-label={t("aria.openLanguageMenu")}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-700 text-slate-200 text-sm hover:border-slate-500 transition-colors"
            >
              <Globe className="h-4 w-4" />
              <span className="uppercase">{activeLang}</span>
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {langOpen && (
              <div
                data-testid="lang-menu"
                className="absolute right-0 mt-2 w-40 bg-slate-900 border border-slate-800 rounded-md shadow-lg overflow-hidden"
              >
                <button
                  data-testid="lang-option-ec"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 transition-colors ${
                    activeLang === "ec" ? "text-white" : "text-slate-300"
                  }`}
                  onClick={() => switchLang("ec")}
                >
                  Español (EC)
                </button>
                <button
                  data-testid="lang-option-en"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 transition-colors ${
                    activeLang === "en" ? "text-white" : "text-slate-300"
                  }`}
                  onClick={() => switchLang("en")}
                >
                  English (EN)
                </button>
              </div>
            )}
          </div>

          <button
            data-testid="header-cta"
            onClick={() => goSection("newsletter")}
            className="hidden sm:inline-flex items-center gap-1 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2 transition-colors"
          >
            {t("nav.getAccess")}
          </button>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-slate-200 border border-slate-800"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden border-t border-slate-800 bg-slate-950"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.key}
                data-testid={`mobile-nav-${link.key}`}
                onClick={() => goSection(link.target)}
                className="text-left px-3 py-3 rounded-md text-slate-200 hover:bg-slate-900 transition-colors"
              >
                {t(`nav.${link.key}`)}
              </button>
            ))}
            <button
              data-testid="mobile-header-cta"
              onClick={() => goSection("newsletter")}
              className="mt-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-semibold px-4 py-3 transition-colors"
            >
              {t("nav.getAccess")}
            </button>
            <div className="mt-4 pt-4 border-t border-slate-800 flex gap-2">
              <button
                data-testid="mobile-lang-ec"
                onClick={() => switchLang("ec")}
                className={`flex-1 px-3 py-2 rounded-md border ${
                  activeLang === "ec"
                    ? "border-blue-500 text-white"
                    : "border-slate-700 text-slate-300"
                }`}
              >
                Español (EC)
              </button>
              <button
                data-testid="mobile-lang-en"
                onClick={() => switchLang("en")}
                className={`flex-1 px-3 py-2 rounded-md border ${
                  activeLang === "en"
                    ? "border-blue-500 text-white"
                    : "border-slate-700 text-slate-300"
                }`}
              >
                English (EN)
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function LogoMark() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
    >
      <rect x="1" y="1" width="30" height="30" rx="7" fill="#0B132B" stroke="#2563EB" strokeWidth="1.5" />
      <path d="M9 22V10h10v2.4h-7.3v2.4h6.6v2.4h-6.6V22H9z" fill="#F9FAFB" />
      <circle cx="23" cy="10" r="1.6" fill="#06B6D4" />
    </svg>
  );
}
